import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Package, Search, X, Loader2, Trash2 } from "lucide-react";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const STATUS_TONE = {
    draft: "bg-zinc-100 text-zinc-700",
    sent: "bg-amber-100 text-amber-800",
    received: "bg-emerald-100 text-emerald-700",
    closed: "bg-zinc-200 text-zinc-700",
};

const STATUS_FILTERS = ["all", "draft", "sent", "received", "closed"];

const AdminPurchaseOrders = () => {
    const navigate = useNavigate();
    const [status, setStatus] = useState("all");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    // Create modal
    const [open, setOpen] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [productSearch, setProductSearch] = useState("");
    const [products, setProducts] = useState([]);
    const [draft, setDraft] = useState({ vendor_id: "", items: [], expected_delivery_date: "", notes: "" });
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true);
        const params = {};
        if (status !== "all") params.status = status;
        api.get("/admin/purchase-orders", { params })
            .then((r) => setItems(r.data.items || []))
            .finally(() => setLoading(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [status]);

    const openNew = async () => {
        setOpen(true);
        setDraft({ vendor_id: "", items: [], expected_delivery_date: "", notes: "" });
        setProductSearch("");
        setProducts([]);
        try {
            const { data } = await api.get("/admin/vendors", { params: { active: true } });
            setVendors(data.items || []);
        } catch {
            toast.error("Could not load vendors");
        }
    };

    const searchProducts = async (e) => {
        e.preventDefault();
        if (!productSearch.trim()) return;
        try {
            const { data } = await api.get("/products", { params: { q: productSearch.trim(), page_size: 10 } });
            setProducts(data.items || []);
        } catch {
            toast.error("Search failed");
        }
    };

    const addItem = (product) => {
        if (draft.items.find((i) => i.product_id === product.id)) {
            toast("Already added");
            return;
        }
        setDraft({
            ...draft,
            items: [
                ...draft.items,
                {
                    product_id: product.id,
                    _name: product.name,
                    _brand: product.brand,
                    _pack: product.pack_size,
                    qty: 10,
                    unit_cost: Math.round((product.selling_price || 0) * 0.7 * 100) / 100,
                },
            ],
        });
    };

    const updateItem = (idx, patch) => {
        const list = [...draft.items];
        list[idx] = { ...list[idx], ...patch };
        setDraft({ ...draft, items: list });
    };

    const removeItem = (idx) => {
        setDraft({ ...draft, items: draft.items.filter((_, i) => i !== idx) });
    };

    const submit = async () => {
        if (!draft.vendor_id) { toast.error("Select a vendor"); return; }
        if (draft.items.length === 0) { toast.error("Add at least one product"); return; }
        for (const it of draft.items) {
            if (!(it.qty >= 1) || !(it.unit_cost >= 0)) { toast.error("Qty / cost invalid"); return; }
        }
        const body = {
            vendor_id: draft.vendor_id,
            items: draft.items.map((i) => ({ product_id: i.product_id, qty: Number(i.qty), unit_cost: Number(i.unit_cost) })),
            expected_delivery_date: draft.expected_delivery_date || null,
            notes: draft.notes || null,
        };
        setSaving(true);
        try {
            const { data } = await api.post("/admin/purchase-orders", body);
            toast.success(`PO ${data.po_no} created`);
            setOpen(false);
            navigate(`/admin/purchase-orders/${data.id}`);
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || d?.code || "Could not create PO");
        } finally {
            setSaving(false);
        }
    };

    const subtotal = draft.items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.unit_cost) || 0), 0);

    return (
        <AdminAuthGuard>
            <AdminLayout title="Purchase orders" subtitle="Replenish stock from vendors">
                <div className="mb-5 flex flex-wrap items-center gap-2">
                    <div className="flex gap-1.5" data-testid="admin-po-tabs">
                        {STATUS_FILTERS.map((s) => (
                            <button
                                key={s}
                                onClick={() => setStatus(s)}
                                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                                    status === s ? "bg-brand-700 text-white" : "border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
                                }`}
                                data-testid={`admin-po-tab-${s}`}
                            >{s}</button>
                        ))}
                    </div>
                    <button onClick={openNew} className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-800" data-testid="admin-po-new">
                        <Plus className="h-4 w-4" /> New PO
                    </button>
                </div>

                {loading ? (
                    <div className="h-48 skeleton rounded-2xl" />
                ) : items.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-zinc-200 bg-white p-12 text-center text-sm text-slate-500" data-testid="admin-po-empty">
                        <Package className="mx-auto h-8 w-8 text-slate-300" />
                        <p className="mt-2">No purchase orders yet. Create one to replenish stock.</p>
                    </div>
                ) : (
                    <div className="overflow-hidden rounded-2xl border border-zinc-100 bg-white shadow-sm">
                        <table className="w-full text-sm" data-testid="admin-po-table">
                            <thead className="border-b border-zinc-100 bg-zinc-50 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                                <tr>
                                    <th className="px-4 py-3">PO #</th>
                                    <th className="px-4 py-3">Vendor</th>
                                    <th className="px-4 py-3">Items</th>
                                    <th className="px-4 py-3">Created</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3 text-right">Total</th>
                                    <th className="px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-50">
                                {items.map((p) => (
                                    <tr key={p.id} className="hover:bg-zinc-50" data-testid={`admin-po-row-${p.id}`}>
                                        <td className="px-4 py-3 font-mono text-xs">{p.po_no}</td>
                                        <td className="px-4 py-3 text-xs">{p.vendor_name || p.vendor_id?.slice(0, 8)}</td>
                                        <td className="px-4 py-3 text-xs">{p.items?.length || 0}</td>
                                        <td className="px-4 py-3 text-xs text-slate-500">{new Date(p.created_at).toLocaleDateString()}</td>
                                        <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_TONE[p.status] || "bg-zinc-100"}`}>{p.status}</span></td>
                                        <td className="px-4 py-3 text-right text-sm font-semibold">{inr(p.total)}</td>
                                        <td className="px-4 py-3 text-right">
                                            <Link to={`/admin/purchase-orders/${p.id}`} className="text-xs text-brand-700 hover:underline" data-testid={`admin-po-open-${p.id}`}>Open →</Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {open && (
                    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
                        <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl" data-testid="admin-po-modal">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="label-uppercase">Purchase order</p>
                                    <h2 className="text-lg font-semibold">Create new PO</h2>
                                </div>
                                <button onClick={() => setOpen(false)} className="rounded-full p-1.5 text-zinc-400 hover:bg-zinc-100"><X className="h-4 w-4" /></button>
                            </div>

                            <div className="mt-4 grid gap-3 sm:grid-cols-2 text-sm">
                                <label>
                                    <span className="text-xs text-zinc-500">Vendor *</span>
                                    <select value={draft.vendor_id} onChange={(e) => setDraft({ ...draft, vendor_id: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5" data-testid="admin-po-vendor">
                                        <option value="">Select vendor…</option>
                                        {vendors.map((v) => <option key={v.id} value={v.id}>{v.name} (Net {v.payment_terms_days})</option>)}
                                    </select>
                                </label>
                                <label>
                                    <span className="text-xs text-zinc-500">Expected delivery</span>
                                    <input type="date" value={draft.expected_delivery_date} onChange={(e) => setDraft({ ...draft, expected_delivery_date: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5" data-testid="admin-po-delivery" />
                                </label>
                            </div>

                            <div className="mt-5">
                                <p className="label-uppercase mb-2">Add products</p>
                                <form onSubmit={searchProducts} className="relative">
                                    <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                                    <input value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder="Search products by name / brand…" className="w-full rounded-md border border-zinc-200 bg-white pl-8 pr-3 py-2 text-sm outline-none focus:border-brand-400" data-testid="admin-po-product-search" />
                                </form>
                                {products.length > 0 && (
                                    <ul className="mt-2 max-h-48 overflow-y-auto rounded-md border border-zinc-100 bg-zinc-50" data-testid="admin-po-product-results">
                                        {products.map((p) => (
                                            <li key={p.id} className="flex items-center justify-between border-b border-zinc-100 px-3 py-2 last:border-b-0 text-sm">
                                                <div>
                                                    <p className="font-medium">{p.name}</p>
                                                    <p className="text-[11px] text-slate-500">{p.brand} · {p.pack_size} · MRP {inr(p.mrp)} · stock {p.stock_qty}</p>
                                                </div>
                                                <button onClick={() => addItem(p)} className="rounded-md bg-brand-700 px-2 py-1 text-xs font-medium text-white hover:bg-brand-800" data-testid={`admin-po-add-product-${p.id}`}>Add</button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {draft.items.length > 0 && (
                                <div className="mt-4 overflow-hidden rounded-md border border-zinc-100">
                                    <table className="w-full text-xs">
                                        <thead className="bg-zinc-50 text-left text-[10px] uppercase tracking-wider text-zinc-500">
                                            <tr>
                                                <th className="px-3 py-2">Product</th>
                                                <th className="px-3 py-2">Qty</th>
                                                <th className="px-3 py-2">Unit cost (₹)</th>
                                                <th className="px-3 py-2 text-right">Subtotal</th>
                                                <th className="px-3 py-2"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {draft.items.map((it, i) => (
                                                <tr key={it.product_id} className="border-t border-zinc-100" data-testid={`admin-po-line-${it.product_id}`}>
                                                    <td className="px-3 py-2">
                                                        <p className="font-medium">{it._name}</p>
                                                        <p className="text-[10px] text-slate-500">{it._brand} · {it._pack}</p>
                                                    </td>
                                                    <td className="px-3 py-2"><input type="number" min="1" value={it.qty} onChange={(e) => updateItem(i, { qty: Number(e.target.value) })} className="w-20 rounded border border-zinc-200 px-2 py-1" data-testid={`admin-po-line-qty-${it.product_id}`} /></td>
                                                    <td className="px-3 py-2"><input type="number" min="0" step="0.01" value={it.unit_cost} onChange={(e) => updateItem(i, { unit_cost: Number(e.target.value) })} className="w-24 rounded border border-zinc-200 px-2 py-1" data-testid={`admin-po-line-cost-${it.product_id}`} /></td>
                                                    <td className="px-3 py-2 text-right font-semibold">{inr((it.qty || 0) * (it.unit_cost || 0))}</td>
                                                    <td className="px-3 py-2 text-right">
                                                        <button onClick={() => removeItem(i)} className="rounded p-1 text-rose-500 hover:bg-rose-50" data-testid={`admin-po-line-remove-${it.product_id}`}><Trash2 className="h-3 w-3" /></button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="border-t border-zinc-100 bg-zinc-50">
                                            <tr>
                                                <td colSpan={3} className="px-3 py-2 text-right font-semibold">Total</td>
                                                <td className="px-3 py-2 text-right font-semibold">{inr(subtotal)}</td>
                                                <td />
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            )}

                            <label className="mt-4 block text-sm">
                                <span className="text-xs text-zinc-500">Notes</span>
                                <textarea rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-200 p-2 text-sm" data-testid="admin-po-notes" />
                            </label>

                            <div className="mt-5 flex items-center justify-end gap-2">
                                <button onClick={() => setOpen(false)} className="rounded-md border border-zinc-200 px-3 py-1.5 text-sm hover:bg-zinc-50">Cancel</button>
                                <button onClick={submit} disabled={saving} className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50" data-testid="admin-po-modal-save">
                                    {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                                    {saving ? "Creating…" : "Create PO"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </AdminLayout>
        </AdminAuthGuard>
    );
};

export default AdminPurchaseOrders;
